import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useApi } from "@/hooks/useApi";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, Users } from "lucide-react";

export const ComparisonPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const api = useApi(token);
  const [patients, setPatients] = useState([]);
  const [selectedPatients, setSelectedPatients] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadPatients();
  }, []);

  const loadPatients = async () => {
    try {
      const data = await api.get("/patients/");
      setPatients(data);
    } catch (error) {
      console.error("Failed to load patients:", error);
    }
  };

  const handlePatientToggle = (patientId) => {
    if (selectedPatients.includes(patientId)) {
      setSelectedPatients(selectedPatients.filter(id => id !== patientId));
    } else {
      if (selectedPatients.length < 5) {
        setSelectedPatients([...selectedPatients, patientId]);
      }
    }
  };

  const handleCompare = async () => {
    if (selectedPatients.length < 2) {
      alert("Please select at least 2 patients to compare");
      return;
    }

    setLoading(true);
    try {
      const data = await api.get(`/files/compare-patients?patient_ids=${selectedPatients.join(',')}`);
      setComparisonData(data);
    } catch (error) {
      console.error("Failed to compare patients:", error);
      alert("Failed to load comparison data");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#fcfbf9", fontFamily: "Poppins, sans-serif" }}>
      <header className="border-b" style={{ backgroundColor: "rgba(255,255,255,0.7)", backdropFilter: "blur(12px)", borderColor: "#e5e5e5" }}>
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button 
            onClick={() => navigate("/dashboard")} 
            variant="ghost" 
            className="rounded-full"
          >
            <ArrowLeft size={20} />
          </Button>
          <h1 className="text-2xl font-bold" style={{ color: "#652D90" }}>
            <Users size={24} className="inline mr-2" />
            Multi-Patient Comparison
          </h1>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <Card className="rounded-2xl border-[#e5e5e5] mb-6">
          <CardHeader>
            <CardTitle style={{ color: "#652D90" }}>Select Patients (2-5)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              {patients.map((patient) => (
                <div
                  key={patient.id}
                  className="flex items-center space-x-2 p-4 rounded-xl border"
                  style={{ borderColor: selectedPatients.includes(patient.id) ? "#652D90" : "#e5e5e5" }}
                >
                  <Checkbox
                    id={patient.id}
                    checked={selectedPatients.includes(patient.id)}
                    onCheckedChange={() => handlePatientToggle(patient.id)}
                    disabled={selectedPatients.length >= 5 && !selectedPatients.includes(patient.id)}
                  />
                  <label htmlFor={patient.id} className="flex-1 cursor-pointer">
                    <div className="font-semibold" style={{ color: "#333333" }}>{patient.full_name}</div>
                    <div className="text-sm" style={{ color: "#555555" }}>
                      {patient.gender} | {patient.date_of_birth || 'DOB N/A'}
                    </div>
                  </label>
                </div>
              ))}
            </div>
            <Button
              onClick={handleCompare}
              disabled={selectedPatients.length < 2 || loading}
              className="rounded-full"
              style={{ backgroundColor: "#652D90" }}
              data-testid="compare-button"
            >
              {loading ? "Comparing..." : "Compare Selected Patients"}
            </Button>
          </CardContent>
        </Card>

        {comparisonData && (
          <Card className="rounded-2xl border-[#e5e5e5]">
            <CardHeader>
              <CardTitle style={{ color: "#652D90" }}>Comparison Results</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b" style={{ borderColor: "#e5e5e5" }}>
                      <th className="text-left py-3 px-2" style={{ color: "#333333" }}>Test Name</th>
                      {comparisonData.patients.map((patient) => (
                        <th key={patient.patient_id} className="text-center py-3 px-2" style={{ color: "#652D90" }}>
                          {patient.patient_name}
                          <div className="text-xs font-normal" style={{ color: "#555555" }}>
                            {new Date(patient.latest_test_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Get all unique test codes */}
                    {(() => {
                      const allTestCodes = new Set();
                      comparisonData.patients.forEach(p => {
                        p.lab_values.forEach(v => allTestCodes.add(v.test_code));
                      });
                      
                      return Array.from(allTestCodes).map(testCode => {
                        const testName = comparisonData.patients
                          .flatMap(p => p.lab_values)
                          .find(v => v.test_code === testCode)?.name || testCode;
                        
                        return (
                          <tr key={testCode} className="border-b" style={{ borderColor: "#e5e5e5" }}>
                            <td className="py-2 px-2 font-medium" style={{ color: "#333333" }}>{testName}</td>
                            {comparisonData.patients.map((patient) => {
                              const testValue = patient.lab_values.find(v => v.test_code === testCode);
                              if (testValue) {
                                const color = testValue.flag === "high" ? "#e90d01" : 
                                            testValue.flag === "low" ? "#1fb25a" : "#333333";
                                return (
                                  <td key={patient.patient_id} className="py-2 px-2 text-center" style={{ color }}>
                                    {testValue.value || testValue.value_str || "N/A"} {testValue.unit}
                                    {testValue.flag && testValue.flag !== "normal" && (
                                      <span className="ml-1 text-xs">({testValue.flag.toUpperCase()})</span>
                                    )}
                                  </td>
                                );
                              }
                              return <td key={patient.patient_id} className="py-2 px-2 text-center" style={{ color: "#999999" }}>—</td>;
                            })}
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
              
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                {comparisonData.patients.map((patient) => (
                  <Card key={patient.patient_id} className="rounded-xl">
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <div className="font-semibold mb-2" style={{ color: "#333333" }}>{patient.patient_name}</div>
                        <div className="text-3xl font-bold" style={{ color: patient.abnormal_count > 0 ? "#e90d01" : "#1fb25a" }}>
                          {patient.abnormal_count}
                        </div>
                        <div className="text-sm" style={{ color: "#555555" }}>Abnormal Values</div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};
